// @ts-nocheck
import React, { useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import { useOutletContext } from 'react-router-dom';
import helpers from '@/utils/helpers';
import { isScoresheetSupportedSport } from '@staty/shared/utils/scoresheetSupport';

import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Camera, AlertTriangle, Trophy, CheckCircle2 } from 'lucide-react';

import useScoresheetStore from '@/store/scoresheetStore';
import useDivisionStore from '@/store/divisionStore';
import { PageContainer, PageHeader } from '@/components/Page/PageContainer';
import { ListSkeleton } from '@/components/Page/PageSkeletons';
import { EmptyState } from '@/components/Page/EmptyState';
import ScoresheetScanDialogs from './ScoresheetScanDialogs';
import GameResultRow from './GameResultRow';

const pad = (n) => String(n).padStart(2, '0');
const keyFromDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

// Group/sort key for a game's calendar date (local), or a sentinel that sorts last.
const dateKeyOf = (g) => {
  const raw = g.globalDate || g.date;
  const d = raw ? new Date(raw) : null;
  if (!d || Number.isNaN(d.getTime())) return 'zzzz-no-date';
  return keyFromDate(d);
};

const dateLabelOf = (g) => {
  const raw = g.globalDate || g.date;
  const d = raw ? new Date(raw) : null;
  if (!d || Number.isNaN(d.getTime())) return 'Date TBD';
  return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
};

// Build tournament sections (contests) + standalone divisions, each division
// grouped by date. A division heading is "<leagueName> / <divisionName>".
const buildStructure = (games, dateSort = 'asc') => {
  const divMap = new Map();
  (games || []).forEach((g) => {
    const divId = helpers.getObjId(g.division);
    if (!divId) return;
    if (!divMap.has(divId)) {
      const isTournament = g.league?.contestType === 'tournament' && g.league?.tournament;
      divMap.set(divId, {
        divisionId: divId,
        leagueName: _.get(g, 'league.name') || '',
        divisionName: _.get(g, 'division.name') || 'Division',
        tournamentId: isTournament ? helpers.getObjId(g.league.tournament) : null,
        tournamentName: isTournament ? (_.get(g, 'league.tournament.name') || 'Tournament') : null,
        dates: new Map(),
      });
    }
    const entry = divMap.get(divId);
    const key = dateKeyOf(g);
    if (!entry.dates.has(key)) entry.dates.set(key, { key, label: dateLabelOf(g), games: [] });
    entry.dates.get(key).games.push(g);
  });

  const tournaments = new Map();
  const standalone = [];
  for (const d of divMap.values()) {
    const dateGroups = [...d.dates.values()].sort((a, b) =>
      dateSort === 'desc' ? b.key.localeCompare(a.key) : a.key.localeCompare(b.key));
    const div = { ...d, dateGroups };
    if (d.tournamentId) {
      if (!tournaments.has(d.tournamentId)) {
        tournaments.set(d.tournamentId, { id: d.tournamentId, name: d.tournamentName, divisions: [] });
      }
      tournaments.get(d.tournamentId).divisions.push(div);
    } else {
      standalone.push(div);
    }
  }
  return { tournaments: [...tournaments.values()], standalone };
};

export default function UpcomingGamesAdmin() {
  const { league: org } = useOutletContext() || {};
  const orgId = helpers.getObjId(org);
  const sport = org?.sports?.[0];
  const supported = isScoresheetSupportedSport(sport);

  const { unscoredGames, getUnscoredByOrganization } = useScoresheetStore((s) => ({
    unscoredGames: s.unscoredGames, getUnscoredByOrganization: s.getUnscoredByOrganization,
  }));
  const { divisions, getDivisionById } = useDivisionStore((s) => ({
    divisions: s.divisions, getDivisionById: s.getDivisionById,
  }));

  // Skeleton only until the FIRST fetch resolves — and only when the store has
  // nothing cached. Never show the empty state while loading (it used to flash
  // "🎉 No games are waiting on results." during the initial fetch).
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (orgId) Promise.resolve(getUnscoredByOrganization(orgId)).finally(() => setLoaded(true));
  }, [orgId]);

  // scan = { type: 'basketball'|'pickleball', game?, divisionId }
  const [scan, setScan] = useState(null);

  useEffect(() => {
    if (scan?.divisionId) getDivisionById(scan.divisionId);
  }, [scan?.divisionId]);

  const scanDivision = useMemo(
    () => (scan?.divisionId ? _.find(divisions, { _id: scan.divisionId }) : null),
    [divisions, scan?.divisionId],
  );

  const refresh = () => orgId && getUnscoredByOrganization(orgId);

  // Partition into Past / Today / Upcoming, then build the tournament→division→date
  // structure within each bucket. Past is shown most-recent-first; the others soonest-first.
  const buckets = useMemo(() => {
    const todayKey = keyFromDate(new Date());
    const past = [], today = [], upcoming = [];
    (unscoredGames || []).forEach((g) => {
      const key = dateKeyOf(g);
      if (key < todayKey) past.push(g);
      else if (key === todayKey) today.push(g);
      else upcoming.push(g); // future + undated (sentinel sorts last)
    });
    return [
      { id: 'past', label: 'Past Games', games: past, structure: buildStructure(past, 'desc') },
      { id: 'today', label: "Today's Games", games: today, structure: buildStructure(today, 'asc') },
      { id: 'upcoming', label: 'Upcoming Games', games: upcoming, structure: buildStructure(upcoming, 'asc') },
    ];
  }, [unscoredGames]);

  const divTitle = (d) => (d.leagueName ? `${d.leagueName} / ${d.divisionName}` : d.divisionName);

  const renderGameRow = (g) => (
    <GameResultRow
      key={g._id}
      game={g}
      sport={sport}
      supported={supported}
      onScanBasketball={(game) => setScan({ type: 'basketball', game, divisionId: helpers.getObjId(game.division) })}
      onSaved={refresh}
    />
  );

  const renderDivisionCard = (d) => (
    <Card key={d.divisionId}>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg">{divTitle(d)}</CardTitle>
        {supported && sport === 'pickleball' && (
          <Button size="sm" onClick={() => setScan({ type: 'pickleball', divisionId: d.divisionId })}>
            <Camera className="h-4 w-4 mr-2" /> Scan Scoresheet
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {d.dateGroups.map((dg) => (
          <div key={dg.key} className="space-y-2">
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{dg.label}</div>
            {dg.games.map(renderGameRow)}
          </div>
        ))}
      </CardContent>
    </Card>
  );

  const renderStructure = (structure) => (
    <>
      {structure.tournaments.map((t) => (
        <section key={t.id} className="space-y-3">
          <div className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-muted-foreground" />
            <h3 className="text-lg font-semibold tracking-tight">{t.name}</h3>
          </div>
          <div className="space-y-4 md:pl-2">{t.divisions.map(renderDivisionCard)}</div>
        </section>
      ))}
      {structure.standalone.map(renderDivisionCard)}
    </>
  );

  const isEmpty = buckets.every((b) => b.games.length === 0);

  return (
    <PageContainer>
      <PageHeader
        title="Game Results"
        description="Games still needing results. Spot missing teams or locations, and scan a scoresheet photo to auto-fill results with AI."
      />

      {!supported && (
        <div className="rounded-md border p-4 text-sm text-muted-foreground flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 mt-0.5" />
          <span>
            AI scoresheet scanning isn&apos;t supported for this sport{sport ? ` (${sport})` : ''} yet.
            You can still see games that need results below; enter scores from the division&apos;s Games page.
          </span>
        </div>
      )}

      {!loaded && isEmpty ? (
        <ListSkeleton />
      ) : isEmpty ? (
        <EmptyState
          icon={CheckCircle2}
          title="All caught up"
          description="No games are waiting on results. New games appear here once they've been played."
        />
      ) : null}

      {buckets.map((bucket) => (
        bucket.games.length === 0 ? null : (
          <section key={bucket.id} className="space-y-4">
            <div className="flex items-center gap-2 border-b pb-2">
              <h2 className="text-2xl font-bold tracking-tight">{bucket.label}</h2>
              <Badge variant="secondary" className="rounded-full">{bucket.games.length}</Badge>
            </div>
            {renderStructure(bucket.structure)}
          </section>
        )
      ))}

      <ScoresheetScanDialogs
        scan={scan}
        division={scanDivision}
        org={org}
        onClose={() => setScan(null)}
        onSubmitted={refresh}
      />
    </PageContainer>
  );
}
